import * as React from 'react';
import { addParamsToSearchUrl, addSortToSearchUrl } from '@/utils/urls';
import { BodyPrimary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { openConfirmUnfollowSearchModal } from '@/redux/modules/modal';
import { SavedSearch } from '@/redux/api/saveSearch';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { shortFormatDate } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { toFacetLabelPairs, toSavedSearchUrl } from '@/utils/savedSearchFacets';
import { useAppDispatch } from '@/redux/hooks';
import { useTheme } from 'styled-components';
import { useTrackSearchUnfollowedAnalytics } from '@/utils/amplitude/analytics';
import isEqual from 'react-fast-compare';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

export interface ManageSavedSearchCardProps {
    search: SavedSearch;
}

const ManageSavedSearchCard = ({ search }: ManageSavedSearchCardProps) => {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const trackSearchUnfollowed = useTrackSearchUnfollowedAnalytics();

    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();
    const isMobileView = useIsNarrowerThan(tabletNarrowWidthNum + 1);
    const searchLabels = toFacetLabelPairs(search.facets, intl);
    const searchInfoString = searchLabels.slice(1).join(', ');
    const createdDate = search.createdDate ? (
        shortFormatDate(Date.parse(search.createdDate) / 1000)
    ) : (
        <FormattedMessage id="never" />
    );
    const lastSentStatus = intl.formatNumber(search.resultCount);
    const savedSearchUrl = search?.searchURL || toSavedSearchUrl(search.facets);
    const savedSearchSortUrl = addSortToSearchUrl(savedSearchUrl, SearchSortAndDirection.NewlyListed);
    const savedSearchSortUrlWithFollowedSearch = addParamsToSearchUrl(savedSearchSortUrl, { followed_search: true });

    const unfollowSearch = () => {
        dispatch(openConfirmUnfollowSearchModal(search.savedSearchId));
        trackSearchUnfollowed();
    };

    return isMobileView ? (
        <div className="flex flex-row items-center justify-between border-b border-border-secondary py-sm">
            <div className="flex flex-col items-start justify-center">
                <BodyPrimary>
                    <Link
                        className="text-left no-underline"
                        href={savedSearchSortUrlWithFollowedSearch}
                        target="_blank"
                    >
                        {searchLabels[0]}
                    </Link>
                </BodyPrimary>
                <BodyPrimary className="mt-2xs text-text-secondary">
                    <FormattedMessage
                        id="search_alerts.mobileItemCount"
                        values={{ count: lastSentStatus }}
                    />
                </BodyPrimary>
                <BodyPrimary className="mt-2xs text-text-secondary">
                    {searchLabels.length === 2 ? (
                        <FormattedMessage id="search_alerts.singleFilter" />
                    ) : (
                        <FormattedMessage
                            id="search_alerts.filterCount"
                            values={{ count: searchLabels.length - 1 }}
                        />
                    )}
                </BodyPrimary>
            </div>

            <div className="flex items-center justify-end">
                <InlineButton onClick={unfollowSearch}>
                    {intl.formatMessage({ id: 'search_alerts.unfollow' })}
                </InlineButton>
            </div>
        </div>
    ) : (
        <div className="grid min-h-[68px] grid-cols-[auto_130px_140px_115px] border-b border-border-secondary py-xs">
            <div className="col-span-1 flex flex-col items-start justify-center py-xs pr-sm">
                <BodyPrimary>
                    <Link
                        className="text-left no-underline"
                        data-test-id="followedSearchItem"
                        href={savedSearchSortUrlWithFollowedSearch}
                        target="_blank"
                    >
                        {searchLabels[0]}
                    </Link>
                </BodyPrimary>
                <Caption>{searchInfoString}</Caption>
            </div>
            <div className="col-span-1 col-start-2 flex items-center justify-end text-right">
                <BodyPrimary className="text-text-secondary">{lastSentStatus}</BodyPrimary>
            </div>
            <div className="col-span-1 col-start-3 flex items-center justify-end">
                <BodyPrimary className="text-text-secondary">{createdDate}</BodyPrimary>
            </div>
            <div className="col-span-1 col-start-4 flex items-center justify-end">
                <InlineButton onClick={unfollowSearch}>
                    {intl.formatMessage({ id: 'search_alerts.unfollow' })}
                </InlineButton>
            </div>
        </div>
    );
};

export default React.memo<ManageSavedSearchCardProps>(ManageSavedSearchCard, isEqual);
